'use client'

import React from 'react'
import StoreBadges from '@/app/components/StoreBadges'
import Image from 'next/image'
import { PlatformNames } from '@/app/components/Platforms'

/**
 * Reusable editorial blocks: a direct answer, takeaways, accessible tables,
 * visible questions and linked sources. These help readers understand evidence;
 * no particular block or schema guarantees a ranking or an AI citation.
 */

export function BlogTLDR({ children }: { children: React.ReactNode }) {
  return (
    <div
      role="note"
      aria-label="Quick answer"
      className="not-prose my-6 rounded-xl border border-[#1E6B47]/30 bg-[#1E6B47]/5 p-5"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-[#1E6B47] mb-2">
        Quick Answer
      </p>
      <div className="text-gray-700 leading-relaxed text-base">{children}</div>
    </div>
  )
}

export function BlogKeyTakeaways({ items }: { items: React.ReactNode[] }) {
  return (
    <div
      aria-label="Key takeaways"
      className="not-prose my-6 rounded-xl bg-white border border-black/5 p-5 shadow-sm"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
        Key Takeaways
      </p>
      <ul className="space-y-2 text-gray-700">
        {items.map((item, i) => (
          <li key={i} className="flex gap-3">
            <span aria-hidden="true" className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#1E6B47]" />
            <span className="leading-relaxed">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export interface BlogFAQItem {
  question: string
  /** Plain-text answer for JSON-LD; keep its meaning consistent with the visible answer. */
  answer: string
  /** Optional richer JSX rendered on the page. Falls back to `answer` if omitted. */
  answerJsx?: React.ReactNode
}

/**
 * Renders a visible FAQ block AND the corresponding FAQPage JSON-LD.
 * Google retired FAQ rich results in May 2026. The visible answers remain useful;
 * this markup makes no promise of a rich result or improved AI citations.
 */
export function BlogFAQ({ items, heading = 'Frequently Asked Questions' }: { items: BlogFAQItem[]; heading?: string }) {
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }

  return (
    <section aria-label={heading} className="mt-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
      />
      <h2 className="text-2xl font-display font-bold text-ink mt-8 mb-4">
        {heading}
      </h2>
      <div className="space-y-5">
        {items.map((item, i) => (
          <div key={i} className="rounded-xl bg-white border border-black/5 p-5">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">{item.question}</h3>
            <div className="text-gray-700 leading-relaxed">{item.answerJsx || item.answer}</div>
          </div>
        ))}
      </div>
    </section>
  )
}

export interface BlogSource {
  /** Authoring entity, e.g. "Leslie HA, et al." or "World Health Organization". */
  authors: string
  /** Publication year, or an explicit undated/access-date label for a live webpage. */
  year: number | string
  /** Title of the paper / report / page. */
  title: string
  /** Publisher / journal, e.g. "Environment International" or "WHO". */
  publisher?: string
  /**
   * Direct URL — should resolve to PubMed / NEJM / WHO / CDC / FDA / NIH / PNAS
   * or similar primary source. Omit only for our own first-party work (e.g. an
   * in-house INCI audit), which has no external URL; the citation then renders
   * as plain text.
   */
  url?: string
}

export function BlogSources({ items }: { items: BlogSource[] }) {
  return (
    <section aria-label="Sources" className="mt-12 pt-8 border-t border-black/10">
      <h2 className="text-xl font-bold text-gray-800 mb-4">Sources</h2>
      <ol className="space-y-3 text-sm text-gray-600 list-decimal pl-6">
        {items.map((source, i) => (
          <li key={i} className="leading-relaxed">
            {source.authors} ({source.year}).{' '}
            {source.url ? (
              <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#1E6B47] hover:text-[#155235] underline"
              >
                {source.title}
              </a>
            ) : (
              <span className="text-gray-800">{source.title}</span>
            )}
            {source.publisher ? `. ${source.publisher}.` : '.'}
          </li>
        ))}
      </ol>
    </section>
  )
}

// Retired, deliberately kept as a no-op rather than deleted.
//
// This printed a THIRD date on every article — the byline already carries the
// publication date and the trust strip directly beneath it carries "Last
// reviewed". It rendered as a bare line floating between the table of contents
// and Key Takeaways, outside any container, while every block around it sat in
// a card. Three representations of one fact, one of them visually orphaned.
//
// It is called from 237 PostContent files, so returning null removes it
// everywhere in one place instead of touching all of them. The machine-readable
// date is unaffected: dateModified still ships in the Article JSON-LD.
export function BlogLastUpdated(_props: { iso: string; formatted: string }) {
  return null
}

interface ComparisonTableColumn {
  key: string
  label: string
}

interface ComparisonTableProps {
  caption?: string
  columns: ComparisonTableColumn[]
  rows: Array<Record<string, React.ReactNode>>
  /** Present long source disclosures as stacked definition lists on phones. */
  mobileCards?: boolean
}

/**
 * Renders a semantic comparison table with a caption and column headings.
 */
export function BlogComparisonTable({ caption, columns, rows, mobileCards = false }: ComparisonTableProps) {
  return (
    <>
    {mobileCards && (
      <div className="not-prose my-8 space-y-4 sm:hidden" aria-label={caption}>
        {caption && <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{caption}</p>}
        {rows.map((row, i) => (
          <dl key={i} className="space-y-4 rounded-xl border border-[#1E6B47]/15 bg-white p-5">
            {columns.map((col, j) => (
              <div key={col.key}>
                <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{col.label}</dt>
                <dd className={`mt-1 break-words leading-relaxed ${j === 0 ? 'font-semibold text-ink' : 'text-sm text-gray-700'}`}>{row[col.key]}</dd>
              </div>
            ))}
          </dl>
        ))}
      </div>
    )}
    <div className={`not-prose my-8 overflow-x-auto${mobileCards ? ' hidden sm:block' : ''}`}>
      <table className="w-full text-sm border-collapse">
        {caption && (
          <caption className="text-left text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">
            {caption}
          </caption>
        )}
        <thead>
          <tr className="bg-[#1E6B47]/10">
            {columns.map((col) => (
              <th
                key={col.key}
                scope="col"
                className="text-left p-3 font-semibold text-gray-800 border-b border-[#1E6B47]/30"
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#F7F5F2]/60'}>
              {columns.map((col) => (
                <td key={col.key} className="p-3 align-top text-gray-700 border-b border-black/5">
                  {row[col.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    </>
  )
}

/**
 * Per-article tailored App Store CTA. The headline + sub should match the
 * article topic (water article → "Scan bottled drinks before you buy",
 * kitchen article → "Build your kitchen exposure baseline", etc).
 */
export function BlogAppCTA({
  headline,
  sub,
  buttonLabel = 'Get the MicroPlastics app',
  source = 'blog_inline_app_cta',
  postSlug,
  queryFamily,
}: {
  headline: string
  sub: string
  buttonLabel?: string
  source?: string
  postSlug?: string
  queryFamily?: string
}) {
  return (
    <div className="not-prose my-8 rounded-2xl bg-gradient-to-br from-[#1E6B47]/15 via-[#1E6B47]/8 to-white border border-[#1E6B47]/30 p-6 md:p-7">
      <div className="mb-3 flex items-center gap-3">
        <Image src="/icon1.png" alt="" width={40} height={40} className="h-10 w-10 rounded-[10px] shadow-sm" />
        <p className="text-xs font-semibold uppercase tracking-wider text-[#1E6B47]">
          Use the App
        </p>
      </div>
      <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">{headline}</h3>
      <p className="text-gray-700 leading-relaxed mb-4">
        {sub.split('iOS and Android').map((part, i) => (
          <React.Fragment key={i}>
            {i > 0 ? <PlatformNames /> : null}
            {part}
          </React.Fragment>
        ))}
      </p>
      <StoreBadges
          source={source}
          ctaPosition="inline_contextual"
          trackingParams={{
            page_type: 'blog',
            cta_copy: buttonLabel,
            ...(postSlug ? { post_slug: postSlug } : {}),
            ...(queryFamily ? { organic_query_family: queryFamily } : {}),
            app_store_campaign_token: source,
          }}
        />
    </div>
  )
}

/**
 * Trust-building "What the app actually checks" box. Names the specific
 * factors evaluated so the reader knows the score isn't a black box.
 * Pass the article-relevant `items` (3-6 short bullets).
 */
export function BlogWhatTheAppChecks({ items }: { items: React.ReactNode[] }) {
  return (
    <div
      aria-label="What the app checks"
      className="not-prose my-6 rounded-xl border border-black/10 bg-white p-5 shadow-sm"
    >
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
        What the MicroPlastics app checks
      </p>
      <ul className="space-y-2 text-gray-700">
        {items.map((item, i) => (
          <li key={i} className="flex gap-3">
            <span aria-hidden="true" className="mt-2 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[#1E6B47]" />
            <span className="leading-relaxed text-sm">{item}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}
